import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Combobox } from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2, Trash2, Pencil, CalendarX2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { orgNowLocal } from "@/lib/tz";

const DAYS = [["Fri", 0], ["Sat", 1], ["Sun", 2], ["Mon", 3], ["Tue", 4], ["Wed", 5], ["Thu", 6]];
const MODE_LABEL = { "": "—", "All Day": "All day", "Custom": "Custom" };
const emptyRange = () => ({ start: "09:00", end: "13:00" });
const hm = (t) => { const s = String(t || ""); return s ? s.padStart(8, "0").slice(0, 5) : ""; };
const key = (w, o) => `${w}-${o}`;

export default function Unavailability() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterWorker, setFilterWorker] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const shownRows = useMemo(() => filterWorker ? rows.filter((r) => r.support_worker === filterWorker) : rows, [rows, filterWorker]);

  const wOpts = useMemo(() => staff.filter((s) => s.status === "Active").map((s) => ({ value: s.name, label: s.display_name || s.name })), [staff]);
  const wName = (id) => staff.find((s) => s.name === id)?.display_name || id;

  useEffect(() => { api.staff().then(setStaff).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    api.unavailability().then(setRows).catch(() => toast.error("Failed to load patterns")).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const nextFriday = () => { let d = orgNowLocal(); while (d.day() !== 5) d = d.add(1, "day"); return d.format("YYYY-MM-DD"); };
  const openNew = () => setEdit({ name: null, support_worker: null, title: "", availability_type: "Unavailable", effective_from: nextFriday(), end_date: "", pattern_weeks: 2, active: true, notes: "", cells: {} });
  const openEdit = (r) => {
    const cells = {};
    (r.slots || []).forEach((s) => {
      const k = key(s.week, s.day_offset);
      if (s.mode === "Custom") {
        const rng = { start: hm(s.start_time) || "09:00", end: hm(s.end_time) || "13:00" };
        if (cells[k] && cells[k].mode === "Custom") cells[k].ranges.push(rng);
        else cells[k] = { mode: "Custom", ranges: [rng] };
      } else if (s.mode === "All Day Except Overnight") {
        // Legacy mode removed — convert to an explicit daytime custom range.
        if (!(cells[k] && cells[k].mode === "All Day")) cells[k] = { mode: "Custom", ranges: [{ start: "06:00", end: "22:00" }] };
      } else {
        cells[k] = { mode: "All Day", ranges: [emptyRange()] };
      }
    });
    setEdit({ name: r.name, support_worker: r.support_worker, title: r.title || "", availability_type: r.availability_type || "Unavailable",
      effective_from: r.effective_from, end_date: r.end_date || "",
      pattern_weeks: r.pattern_weeks || 2, active: !!r.active, notes: r.notes || "", cells });
  };

  const cellOf = (e, w, o) => e.cells[key(w, o)] || { mode: "", ranges: [emptyRange()] };
  const setCell = (w, o, patch) => setEdit((e) => ({ ...e, cells: { ...e.cells, [key(w, o)]: { ...cellOf(e, w, o), ...patch } } }));
  const setRange = (w, o, idx, patch) => setEdit((e) => {
    const c = cellOf(e, w, o);
    const ranges = (c.ranges || []).map((r, i) => (i === idx ? { ...r, ...patch } : r));
    return { ...e, cells: { ...e.cells, [key(w, o)]: { ...c, ranges } } };
  });
  const addRange = (w, o) => setEdit((e) => {
    const c = cellOf(e, w, o);
    return { ...e, cells: { ...e.cells, [key(w, o)]: { ...c, ranges: [...(c.ranges || []), emptyRange()] } } };
  });
  const removeRange = (w, o, idx) => setEdit((e) => {
    const c = cellOf(e, w, o);
    const ranges = (c.ranges || []).filter((_, i) => i !== idx);
    return { ...e, cells: { ...e.cells, [key(w, o)]: { ...c, ranges: ranges.length ? ranges : [emptyRange()] } } };
  });

  const withinNotice = edit && edit.effective_from && dayjs(edit.effective_from).diff(orgNowLocal(), "day") < 30 && dayjs(edit.effective_from).diff(orgNowLocal(), "day") >= 0;

  const save = async () => {
    const e = edit;
    if (!e.support_worker) return toast.error("Choose a worker");
    if (!e.effective_from) return toast.error("Set the Friday Week 1 starts");
    if (dayjs(e.effective_from).day() !== 5) return toast.error("Week 1 must start on a Friday");
    const slots = [];
    let customMissing = false;
    Object.entries(e.cells).forEach(([k, v]) => {
      if (!v.mode) return;
      const [w, o] = k.split("-").map(Number);
      if (v.mode === "All Day") {
        slots.push({ week: w, day_offset: o, mode: "All Day", start_time: null, end_time: null });
      } else if (v.mode === "Custom") {
        const valid = (v.ranges || []).filter((r) => r.start && r.end);
        if (valid.length === 0) { customMissing = true; return; }
        valid.forEach((r) => slots.push({ week: w, day_offset: o, mode: "Custom", start_time: r.start + ":00", end_time: r.end + ":00" }));
      }
    });
    if (customMissing) return toast.error("Each custom day needs at least one start and end time");
    if (slots.length === 0) return toast.error("Mark at least one unavailable day");
    setSaving(true);
    const body = { support_worker: e.support_worker, title: e.title || "", availability_type: e.availability_type || "Unavailable",
      effective_from: e.effective_from, end_date: e.end_date || null,
      pattern_weeks: Number(e.pattern_weeks), active: e.active, notes: e.notes, slots };
    try {
      let res;
      if (e.name) res = await api.updateUnavailability(e.name, body);
      else res = await api.createUnavailability(body);
      const n = res?.auto_unassigned?.length || 0;
      toast.success(e.name ? "Pattern updated" : "Pattern saved");
      if (n > 0) toast.info(`${n} published shift${n === 1 ? "" : "s"} unassigned — now in the unassigned queue`);
      setEdit(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save pattern"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    try { await api.deleteUnavailability(r.name); toast.success("Pattern deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const weeks = edit ? Array.from({ length: Number(edit.pattern_weeks) }, (_, i) => i + 1) : [];

  return (
    <div className="p-4">
      <div className="bg-white border border-slate-200 rounded-lg mb-3">
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <CalendarX2 className="h-4 w-4 text-slate-500" />
            <div>
              <div className="text-sm font-bold text-slate-900">Ongoing Unavailability</div>
              <div className="text-[10px] text-slate-400">Recurring Fri–Thu patterns (office only). Marked cells make the worker unavailable and hard-block rostering automatically.</div>
            </div>
          </div>
          {can.has("unavailability.edit") && (
            <div className="flex items-center gap-2">
              <div className="w-48"><Combobox testid="unavail-filter-worker" options={wOpts} value={filterWorker} onChange={setFilterWorker} placeholder="All workers" /></div>
              <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: "#bf3b4f" }} onClick={openNew} data-testid="add-unavailability-button">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Pattern
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : shownRows.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-400" data-testid="unavail-empty">No unavailability patterns.</div>
        ) : (
          <table className="w-full text-xs" data-testid="unavail-table">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2">Worker</th><th className="px-2 py-2">Title</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Week 1 starts</th><th className="px-2 py-2">Ends</th>
              <th className="px-2 py-2">Weeks</th><th className="px-2 py-2">Slots</th><th className="px-2 py-2">Active</th><th className="px-3 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {shownRows.map((r) => (
                <tr key={r.name} className="border-t border-slate-100" data-testid={`unavail-row-${r.name}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{r.support_worker_name || r.support_worker}</td>
                  <td className="px-2 py-2 text-slate-600">{r.title || <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-2">
                    {r.availability_type === "Maybe Available"
                      ? <span className="rounded px-1.5 py-0.5 text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">Maybe available</span>
                      : <span className="rounded px-1.5 py-0.5 text-[9px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">Unavailable</span>}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-600">{dayjs(r.effective_from).format("ddd D MMM YYYY")}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-500">{r.end_date ? dayjs(r.end_date).format("D MMM YYYY") : "Ongoing"}</td>
                  <td className="px-2 py-2 text-slate-600">{r.pattern_weeks}</td>
                  <td className="px-2 py-2 text-slate-600">{(r.slots || []).length}</td>
                  <td className="px-2 py-2">{r.active ? <span className="text-teal-600 font-semibold">Active</span> : <span className="text-slate-400">Off</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {can.has("unavailability.edit") && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(r)} data-testid={`unavail-edit-${r.name}`}><Pencil className="h-3 w-3" /></Button>}
                      {can.has("unavailability.delete") && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => remove(r)} data-testid={`unavail-delete-${r.name}`}><Trash2 className="h-3 w-3" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-3xl" data-testid="unavail-dialog">
          <DialogHeader><DialogTitle className="text-base">{edit?.name ? "Edit unavailability pattern" : "Add unavailability pattern"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Support worker</label>
                  <div className="mt-1"><Combobox testid="unavail-worker" options={wOpts} value={edit.support_worker} onChange={(v) => setEdit((e) => ({ ...e, support_worker: v }))} placeholder="Select worker" /></div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Pattern length</label>
                  <select value={edit.pattern_weeks} onChange={(e) => setEdit((s) => ({ ...s, pattern_weeks: Number(e.target.value) }))} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="unavail-weeks">
                    <option value={1}>1 week (repeats weekly)</option>
                    <option value={2}>2 weeks (fortnightly)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-medium text-slate-600">Week 1 starts (Friday)</label><Input type="date" value={edit.effective_from} onChange={(e) => setEdit((s) => ({ ...s, effective_from: e.target.value }))} className="h-9 text-xs mt-1" data-testid="unavail-from" /></div>
                <div><label className="text-[11px] font-medium text-slate-600">End date (blank = ongoing)</label><Input type="date" value={edit.end_date} min={edit.effective_from} onChange={(e) => setEdit((s) => ({ ...s, end_date: e.target.value }))} className="h-9 text-xs mt-1" data-testid="unavail-end" /></div>
              </div>
              {withinNotice && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="unavail-30day-note">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  This change starts within the 30-day notice period. It still saves — confirm any urgent changes with the office.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Title / subject</label>
                  <Input value={edit.title} onChange={(e) => setEdit((s) => ({ ...s, title: e.target.value }))} placeholder="e.g. Uni semester, School run" className="h-9 text-xs mt-1" data-testid="unavail-title" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Availability type</label>
                  <select value={edit.availability_type} onChange={(e) => setEdit((s) => ({ ...s, availability_type: e.target.value }))} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="unavail-type">
                    <option value="Unavailable">Unavailable (hard block)</option>
                    <option value="Maybe Available">Maybe available (call first)</option>
                  </select>
                </div>
              </div>
              {edit.availability_type === "Maybe Available" && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="unavail-maybe-note">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Rostering will warn (not block) for these times. Staff can roster the worker but must confirm they've spoken with them first.
                </div>
              )}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs text-slate-700"><Switch checked={edit.active} onCheckedChange={(v) => setEdit((s) => ({ ...s, active: v }))} data-testid="unavail-active" /> Active</label>
              </div>

              <div className="rounded-md border border-slate-200 overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead><tr className="bg-slate-50 text-slate-500">
                    <th className="px-2 py-1.5 text-left">Day</th>
                    {weeks.map((w) => <th key={w} className="px-2 py-1.5 text-left border-l border-slate-100">Week {w}</th>)}
                  </tr></thead>
                  <tbody>
                    {DAYS.map(([lb, off]) => (
                      <tr key={off} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-semibold text-slate-600 whitespace-nowrap">{lb}</td>
                        {weeks.map((w) => {
                          const c = edit.cells[key(w, off)] || { mode: "", ranges: [emptyRange()] };
                          return (
                            <td key={w} className="px-2 py-1.5 border-l border-slate-100 align-top">
                              <select value={c.mode} onChange={(ev) => setCell(w, off, { mode: ev.target.value })}
                                className={`h-7 text-[10px] rounded border px-1 w-full ${c.mode ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-500"}`}
                                data-testid={`unavail-cell-${w}-${off}`}>
                                {Object.entries(MODE_LABEL).map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                              </select>
                              {c.mode === "Custom" && (
                                <div className="mt-1 space-y-1" data-testid={`unavail-cell-ranges-${w}-${off}`}>
                                  {(c.ranges || []).map((rg, idx) => (
                                    <div key={idx} className="flex items-center gap-1">
                                      <Input type="time" value={rg.start} onChange={(ev) => setRange(w, off, idx, { start: ev.target.value })} className="h-7 text-[10px] px-1" data-testid={`unavail-cell-start-${w}-${off}-${idx}`} />
                                      <Input type="time" value={rg.end} onChange={(ev) => setRange(w, off, idx, { end: ev.target.value })} className="h-7 text-[10px] px-1" data-testid={`unavail-cell-end-${w}-${off}-${idx}`} />
                                      <button type="button" onClick={() => removeRange(w, off, idx)} title="Remove time range"
                                        className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`unavail-cell-removerange-${w}-${off}-${idx}`}>
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                  <button type="button" onClick={() => addRange(w, off)} data-testid={`unavail-cell-addrange-${w}-${off}`}
                                    className="flex items-center gap-1 text-[10px] font-semibold text-[#bf3b4f] hover:text-[#a32f42]">
                                    <Plus className="h-3 w-3" /> Add time range
                                  </button>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-slate-400">A marked day means the worker is <b>unavailable</b>. Choose <b>All day</b> or <b>Custom</b> to set one or more time ranges (e.g. 00:00–08:00 and 20:00–00:00 on the same day). Blank = available as normal.</p>
              <div><label className="text-[11px] font-medium text-slate-600">Notes (optional)</label><Textarea value={edit.notes} onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))} className="text-xs mt-1 min-h-[50px]" data-testid="unavail-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={save} disabled={saving} data-testid="unavail-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save pattern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
